import { PmpListColumns, usePmpListModel } from './PmpListModel';
import { useCallback, useMemo } from 'react';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faEye, faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import i18n from 'i18n';
import { Link } from 'react-router-dom';
import { Page } from 'components/Page/Page';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import { PmpDurationError } from './PmpDurationValidator';
import { Pmp } from 'core/pmp/Pmp';
import { Modal } from 'components/common/Modal/Modal';
import { SortDescriptor } from 'components/TableColumn/TableColumn';

export type PmpListProps = {
  spaceOptions: SelectOptions[];
};

export const PmpList: React.FunctionComponent<PmpListProps> = ({
  spaceOptions
}) => {
  const {
    loading,
    columns,
    pageable,
    filterdPmps,
    selectedPmps,
    searchString,
    batchOperations,
    pmpDurationError,
    clearSelect,
    isPmpEditable,
    cancelActivate,
    onHandleSearch,
    onHandleSelect,
    handleTableChange,
    onHandleSelectPage
  } = usePmpListModel(spaceOptions, false);

  const renderRowBtns = useCallback(
    (pmp: Pmp) => {
      const editable = isPmpEditable(pmp);
      return [
        <IconWithTooltip
          key={`edit-btn-${pmp.id}`}
          disabled={!editable}
          icon={faPencilAlt}
          tooltipProps={{
            id: `edit-pmp-${pmp.id}`,
            link: `/pmp/${pmp.id}/edit`,
            tooltip: editable
              ? i18n.t('pmpSetupFlowPage.titles.edit')
              : i18n.t('pmpList.hints.cannotEdit')
          }}
        />,
        <IconWithTooltip
          key={`summary-btn-${pmp.id}`}
          icon={faEye}
          tooltipProps={{
            id: `summary-pmp-${pmp.id}`,
            tooltip: i18n.t('campaignList.labels.summaryCampaign'),
            link: `/pmp/${pmp.id}/summary`
          }}
        />
      ];
    },
    [isPmpEditable]
  );

  const renderListOperations = useCallback(
    () => [
      <Link key='new-btn' to={'/pmp/new'} className={'btn btn-tertiary btn-sm'}>
        {i18n.t('pmpList.buttons.create')}
      </Link>
    ],
    []
  );

  const renderActivateError = useCallback(
    (errorData: PmpDurationError & Pmp) => {
      return (
        <Modal
          title={i18n.t('pmpActivateModal.titles.sponsorshipConflict')}
          primaryButton={{
            title: i18n.t('common.buttons.ok'),
            callback: cancelActivate
          }}
          dismiss={cancelActivate}
          animation={false}
        >
          <div>
            <div>{i18n.t('pmpActivateModal.hints.conflictWith')}</div>
            <ul style={{ paddingInlineStart: '20px' }}>
              {errorData.duration && (
                <li>{i18n.t('pmpActivateModal.hints.durationConflict')}</li>
              )}
              <li>{i18n.t('pmpActivateModal.hints.spaceConflict')}</li>
            </ul>
            <div>
              {errorData.campaignId
                ? i18n.t('pmpActivateModal.hints.boundPmpSuggest')
                : i18n.t('pmpActivateModal.hints.unboundPmpSuggest')}
            </div>
          </div>
        </Modal>
      );
    },
    [cancelActivate]
  );

  const defaultSorted: SortDescriptor = useMemo(
    () => [
      {
        order: 'desc',
        dataField: PmpListColumns.NAME
      }
    ],
    []
  );

  return (
    <Page title={i18n.t('pmpList.title')}>
      {loading && <LoadingIndicator />}
      {pmpDurationError && renderActivateError(pmpDurationError)}
      <StickableTanstackTable
        remote
        customPadding={false}
        keyField={'id'}
        data={filterdPmps}
        columns={columns}
        noDataIndication={i18n.t('pmpList.placeholders.noData')}
        pagination={{
          page: pageable.page,
          sizePerPage: pageable.sizePerPage,
          totalSize: pageable.totalCount
        }}
        selectedRows={selectedPmps}
        onSelect={onHandleSelect}
        onSelectPage={onHandleSelectPage}
        defaultSearchString={searchString}
        searchbarPlaceholder={i18n.t('pmpList.placeholders.searchbar')}
        batchOperaionConfig={batchOperations}
        onHandleSearch={onHandleSearch}
        renderListOperations={renderListOperations}
        renderRowBtns={renderRowBtns}
        clearSelectedRows={clearSelect}
        onTableChange={handleTableChange}
        defaultSorted={defaultSorted}
      />
    </Page>
  );
};
